import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, Check, X, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const Card = ({ title, children, testId }) => (
  <section className="rounded-3xl bg-white border border-stone-200 p-6 space-y-4" data-testid={testId}>
    <h2 className="text-xl font-bold">{title}</h2>
    {children}
  </section>
);

export default function Friends() {
  const [fr, setFr] = useState({ friends: [], incoming: [], outgoing: [] });
  const [access, setAccess] = useState([]);
  const [privacy, setPrivacy] = useState([]);
  const [email, setEmail] = useState("");
  const load = () => {
    api.get("/friends").then((r) => setFr(r.data));
    api.get("/access-requests").then((r) => setAccess(r.data));
    api.get("/privacy").then((r) => setPrivacy(r.data));
  };
  useEffect(load, []);
  const run = async (fn, msg) => { try { await fn(); if (msg) toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };

  const add = (e) => {
    e.preventDefault();
    run(async () => { const { data } = await api.post("/friends/request", { email }); setEmail(""); toast.success(data.status === "accepted" ? "You're now friends" : "Request sent. They need to accept."); });
  };

  return (
    <div className="max-w-6xl mx-auto px-5 py-10 lg:py-14 space-y-8" data-testid="friends-page">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#23603F]">You decide who sees what</p>
        <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold tracking-tight">Friends & Privacy</h1>
        <p className="mt-2 text-stone-600 max-w-2xl">Being friends doesn't give anyone access to anything. You choose which friends see each plan, and you can take it back anytime.</p>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <Card title="Friends" testId="friends-card">
          <form onSubmit={add} className="flex gap-2">
            <input data-testid="friend-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Friend's account email" className="flex-1 min-w-0 h-10 rounded-lg border border-stone-300 px-3 text-sm" />
            <button data-testid="friend-request-btn" className="flex items-center gap-1.5 bg-[#163A2B] hover:bg-[#0F2A1F] text-white text-sm font-semibold px-4 rounded-lg"><UserPlus className="w-4 h-4" /> Request</button>
          </form>
          {fr.incoming.map((r, i) => (
            <div key={r.request_id} data-testid={`friend-incoming-${i}`} className="flex items-center justify-between rounded-xl bg-[#EAF4EC] px-3 py-2.5">
              <span className="text-sm"><strong>{r.user.name}</strong> wants to be friends</span>
              <span className="flex gap-1">
                <button data-testid={`friend-accept-${i}`} onClick={() => run(() => api.post(`/friends/requests/${r.request_id}/accept`), "Friend added")} className="p-2 rounded-lg bg-[#163A2B] text-white"><Check className="w-4 h-4" /></button>
                <button data-testid={`friend-decline-${i}`} onClick={() => run(() => api.post(`/friends/requests/${r.request_id}/decline`))} className="p-2 rounded-lg border"><X className="w-4 h-4" /></button>
              </span>
            </div>
          ))}
          {fr.outgoing.map((r, i) => (
            <div key={r.request_id} data-testid={`friend-outgoing-${i}`} className="flex items-center justify-between text-sm text-stone-500 px-1">
              <span>Waiting on {r.user.name}</span>
              <button onClick={() => run(() => api.post(`/friends/requests/${r.request_id}/cancel`))} className="text-xs underline">Cancel</button>
            </div>
          ))}
          <ul className="divide-y divide-stone-100">
            {fr.friends.length === 0 && <li className="text-sm text-stone-500 py-2">No friends yet. Friends need an Outbound account.</li>}
            {fr.friends.map((f, i) => (
              <li key={f.id} data-testid={`friend-row-${i}`} className="flex items-center justify-between py-2.5">
                <span><span className="font-semibold text-sm">{f.name}</span><span className="block text-xs text-stone-500">{f.email}</span></span>
                <button data-testid={`friend-remove-${i}`} onClick={() => run(() => api.delete(`/friends/${f.id}`), `Removed ${f.name} and their access to your plans`)} className="text-xs text-stone-500 hover:text-red-700">Remove</button>
              </li>
            ))}
          </ul>
        </Card>
        <Card title="Access requests from invite links" testId="access-requests-card">
          {access.length === 0 && <p className="text-sm text-stone-500" data-testid="access-requests-empty">No pending requests. Invite links can only ask. You approve every person.</p>}
          {access.map((a, i) => (
            <div key={a.id} data-testid={`access-request-${i}`} className="flex items-center justify-between rounded-xl border border-stone-200 px-3 py-2.5">
              <span className="text-sm"><strong>{a.requester?.name}</strong> asked to see <em>{a.plan_title}</em></span>
              <span className="flex gap-1">
                <button data-testid={`access-approve-${i}`} onClick={() => run(() => api.post(`/access-requests/${a.id}/approve`), "Access approved")} className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#163A2B] text-white">Approve</button>
                <button data-testid={`access-decline-${i}`} onClick={() => run(() => api.post(`/access-requests/${a.id}/decline`), "Declined")} className="text-xs font-semibold px-3 py-1.5 rounded-lg border">Decline</button>
              </span>
            </div>
          ))}
        </Card>
      </div>
      <Card title={<span className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#163A2B]" /> Sharing & privacy</span>} testId="privacy-card">
        <p className="text-sm text-stone-600">Everyone who can see each of your plans. Removing someone hides the plan from them right away. Live location sharing is not part of this version.</p>
        {privacy.length === 0 && <p className="text-sm text-stone-500">No saved plans yet.</p>}
        <div className="divide-y divide-stone-100">
          {privacy.map((p, i) => (
            <div key={p.id} data-testid={`privacy-plan-${i}`} className="py-3 flex flex-col md:flex-row md:items-center gap-2">
              <div className="md:w-1/3 min-w-0">
                <Link to={`/plans/${p.id}`} className="font-semibold hover:underline truncate block">{p.title}</Link>
                <span className="text-xs text-stone-500 flex items-center gap-1">{p.show_start ? <><Eye className="w-3 h-3" /> Start point visible to viewers</> : <><EyeOff className="w-3 h-3" /> Start point hidden</>}</span>
              </div>
              <div className="flex-1 flex flex-wrap gap-2">
                {p.viewers.length === 0 ? <span className="text-xs font-mono bg-stone-100 text-stone-600 rounded-full px-2.5 py-1">Only you</span> : p.viewers.map((v) => (
                  <span key={v.id} className="flex items-center gap-1 text-xs font-semibold bg-[#E3EFE6] text-[#0F2A1F] rounded-full pl-2.5 pr-1 py-1">
                    {v.name}
                    <button data-testid={`privacy-revoke-${i}-${v.email.split("@")[0]}`} title={`Remove ${v.name}`} onClick={() => run(() => api.delete(`/plans/${p.id}/share/${v.id}`), `${v.name} can no longer see this plan`)} className="p-0.5 rounded-full hover:bg-white"><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
